import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { clearCart } from "../redux/slices/cartSlice";
import { placeOrderRequest, clearOrderState, createDeliveryQuoteRequest, clearDeliveryQuoteState } from "../redux/slices/orderSlice";
import { transformCartToOrderRequest } from "../utils/orderUtils";
import { FormData, OrderType } from "./useCheckoutForm";

export const useOrderManagement = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { items: cartItems } = useAppSelector((state) => state.cart);
  const { selectedRestaurant } = useAppSelector((state) => state.restaurant);
  const { 
    loading: orderLoading, 
    error: orderError, 
    orderResponse,
    deliveryQuoteLoading,
    deliveryQuoteError,
    deliveryQuoteResponse 
  } = useAppSelector((state) => state.order);

  // Payment popup management
  const popupRef = useRef<Window | null>(null);
  const paymentRef = useRef<any>(null);
  const isPaymentInProgress = useRef(false);
  
  // Store form data and delivery fee for 2-step delivery flow
  const deliveryFormDataRef = useRef<{ formData: FormData; total: number; deliveryFee: number } | null>(null);

  const [submittingMethod, setSubmittingMethod] = useState<
    "takeout" | "pay_online" | "delivery" | null
  >(null);
  const [showTakeoutSuccessPopup, setShowTakeoutSuccessPopup] = useState(false);
  const [showVerifyingPaymentPopup, setShowVerifyingPaymentPopup] = useState(false);
  const [showPaymentFailedPopup, setShowPaymentFailedPopup] = useState(false);
  const [showPaymentFailedProcessingPopup, setShowPaymentFailedProcessingPopup] = useState(false);
  const [showPaymentSuccessPopup, setShowPaymentSuccessPopup] = useState(false);
  const [showDeliveryQuoteModal, setShowDeliveryQuoteModal] = useState(false);
  const [showDeliverySuccessPopup, setShowDeliverySuccessPopup] = useState(false);
  const [savedDeliveryQuote, setSavedDeliveryQuote] = useState<{ deliveryId: string; deliveryChargeCents: number } | null>(null);

  // Function to open payment popup with payment link
  const openPaymentPopup = useCallback((paymentLink: string) => {
    if (!paymentLink) {
      console.error("No payment link provided");
      return;
    }

    // Prevent opening multiple popups
    if (popupRef.current && !popupRef.current.closed) {
      console.log("Payment popup already open, ignoring request");
      return popupRef.current;
    }

    console.log("Opening payment popup with link:", paymentLink);
    
    // Reset payment reference before opening new popup
    paymentRef.current = null;
    
    // Calculate popup position
    const width = 800;
    const height = 600;
    const left = (screen.width / 2) - (width / 2);
    const top = (screen.height / 2) - (height / 2);
    
    // Open the popup using window.open with screen positioning
    const popup = window.open(
      paymentLink, 
      'PaymentWindow', 
      `scrollbars=yes,width=${width},height=${height},top=${top},left=${left}`
    );
    
    if (!popup) {
      console.error("Failed to open popup - popup blocker might be active");
      // If popup failed to open, show failed popup immediately
      setTimeout(() => {
        setShowVerifyingPaymentPopup(false);
        setShowPaymentFailedPopup(true);
        isPaymentInProgress.current = false;
      }, 100);
      return null;
    }
    
    // Store the popup reference
    popupRef.current = popup;
    
    console.log("Popup opened successfully, starting monitoring");
    
    // Enhanced popup monitoring with multiple approaches
    let checkInterval: number;
    let timeoutId: number;
    
    // Method 1: Regular interval checking
    checkInterval = setInterval(() => {
      try {
        if (popup && popup.closed) {
          console.log("Popup detected as closed via interval check");
          clearInterval(checkInterval);
          clearTimeout(timeoutId);
          
          if (paymentRef.current) {
            // Payment completed - cleanup
            console.log("Payment completed, cleaning up");
            popupRef.current = null;
            paymentRef.current = null;
            isPaymentInProgress.current = false;
          } else {
            // Payment window manually closed without completion
            console.log("Payment window manually closed - showing failed popup");
            popupRef.current = null;
            paymentRef.current = null;
            isPaymentInProgress.current = false;
            
            // Show failed popup when payment popup is manually closed
            setTimeout(() => {
              setShowVerifyingPaymentPopup(false);
              setShowPaymentFailedPopup(true);
            }, 100);
          }
        }
      } catch (error) {
        console.error("Error checking popup status:", error);
      }
    }, 500); // Check every 500ms for better responsiveness
    
    // Method 2: Timeout fallback (in case interval fails)
    timeoutId = setTimeout(() => {
      try {
        if (popup && popup.closed && !paymentRef.current) {
          console.log("Popup timeout - assuming manual close");
          clearInterval(checkInterval);
          
          popupRef.current = null;
          paymentRef.current = null;
          isPaymentInProgress.current = false;
          
          setTimeout(() => {
            setShowVerifyingPaymentPopup(false);
            setShowPaymentFailedPopup(true);
          }, 100);
        }
      } catch (error) {
        console.error("Error in timeout check:", error);
      }
    }, 30000); // 30 second timeout
    
    return popup;
  }, []);

  // Function to close payment popup
  const closePopup = useCallback(() => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  // Handle OAuth popup messages
  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      console.log("Payment EVENT", event);
      
      // Handle IPOS payment response
      if (event.data.type === 'IPOS_PAYMENT') {
        const { responseMessage, transactionReferenceId, responseCode, amount } = event.data.payload;
        paymentRef.current = { responseMessage, transactionReferenceId, responseCode, amount, paymentType: "ipos" };
        console.log("IPOS PAYMENT", responseCode)
        // Handle iPos payment response
        closePopup();
        setShowVerifyingPaymentPopup(false);
        isPaymentInProgress.current = false;
        
        // Check if payment is successful (response code 200 only)
        if (responseCode == 200) {
          console.log("Payment Success");
          setShowPaymentSuccessPopup(true);
        } else {
          console.log("Payment Failed");
          setShowPaymentFailedProcessingPopup(true);
        }
      }
      if (event.data.type === 'CLOVER_PAYMENT') {
        const { payment_status } = event.data.payload;
        paymentRef.current = { payment_status, paymentType: "clover" };
        
        // Handle iPos payment response
        closePopup();
        setShowVerifyingPaymentPopup(false);
        isPaymentInProgress.current = false;
        
        // Check if payment is successful (response code 200 only)
        if (payment_status?.toLowerCase() == 'success') {
          console.log("Payment Success");
          setShowPaymentSuccessPopup(true);
        } else {
          console.log("Payment Failed");
          setShowPaymentFailedProcessingPopup(true);
        }
      }

      if (event.data.type === 'SQUARE_PAYMENT') {
        const { transactionId, orderId } = event.data.payload;
        paymentRef.current = { transactionId, orderId, paymentType: "square" };

        // Handle Square payment response
        closePopup();
        setShowVerifyingPaymentPopup(false);
        setShowPaymentSuccessPopup(true);
        isPaymentInProgress.current = false;
        console.log("SUCCESS")
      }
      
      // Handle payment status check message (existing functionality)
      if (event.data.type === 'PAYMENT_STATUS_CHECK') {
        const { transaction_id } = event.data.payload;
        console.log("Received transaction_id:", transaction_id);
        paymentRef.current = { transaction_id, paymentType: "status_check" };
        
        // Handle the transaction ID
        closePopup();
        setShowVerifyingPaymentPopup(false);
        isPaymentInProgress.current = false;
        setShowPaymentSuccessPopup(true);
      }
    };

    window.addEventListener('message', handleMessage, false);
    
    return () => {
      window.removeEventListener('message', handleMessage, false);
    };
  }, [closePopup]);

  // Cleanup popup monitoring when component unmounts
  useEffect(() => {
    return () => {
      if (popupRef.current && !popupRef.current.closed) {
        popupRef.current.close();
      }
    };
  }, []);

  // Function to initiate payment
  const initiatePayment = useCallback((paymentLink: string) => {
    console.log("initiatePayment called with:", paymentLink);
    console.log("isPaymentInProgress.current:", isPaymentInProgress.current);
    console.log("popupRef.current:", popupRef.current);
    console.log("popup closed status:", popupRef.current ? popupRef.current.closed : 'no popup');
    
    // Prevent multiple payment windows - enhanced check
    if (isPaymentInProgress.current) {
      console.log("Payment already in progress, ignoring initiate request");
      return;
    }
    
    // Check if popup is already open
    if (popupRef.current && !popupRef.current.closed) {
      console.log("Payment popup already open, ignoring initiate request");
      return;
    }
    
    if (!paymentLink) {
      console.log("No payment link provided");
      return;
    }
    
    console.log("Starting payment process...");
    
    // Set payment in progress FIRST to prevent multiple calls
    isPaymentInProgress.current = true;
    
    // Reset popup states
    setShowVerifyingPaymentPopup(false);
    setShowPaymentFailedPopup(false);
    setShowPaymentFailedProcessingPopup(false);
    setShowPaymentSuccessPopup(false);
    paymentRef.current = null;
    
    // Show the verifying payment status popup
    setShowVerifyingPaymentPopup(true);
    console.log("Set verifying popup to true");
    
    // Open payment popup
    const popup = openPaymentPopup(paymentLink);
    console.log("Payment popup opened:", popup);
    
    // If popup failed to open, reset the progress flag
    if (!popup) {
      console.log("Popup failed to open, resetting progress flag");
      isPaymentInProgress.current = false;
    }
  }, [openPaymentPopup]);

  // ✅ Handle API response after order
  useEffect(() => {
    if (orderResponse && !orderLoading) {
      if (orderResponse.payment_link && (submittingMethod === "pay_online" || submittingMethod === "delivery")) {
        // Redirect to payment for both pay_online and delivery flows
        window.location.href = orderResponse.payment_link;
      } else if (submittingMethod === "delivery" && !orderResponse.payment_link) {
        // For delivery orders without payment link, show success popup
        setShowDeliverySuccessPopup(true);
      } else if (!orderResponse.payment_link && submittingMethod === "takeout") {
        setShowTakeoutSuccessPopup(true);
      }

      setSubmittingMethod(null);
      dispatch(clearOrderState());
    }
  }, [
    orderResponse,
    orderLoading,
    submittingMethod,
    dispatch,
    initiatePayment,
  ]);

  // ✅ Handle API errors
  useEffect(() => {
    if (orderError && !orderLoading) {
      console.error("Order error:", orderError);
      setSubmittingMethod(null);
    }
  }, [orderError, orderLoading]);

  const handleOrderSubmit = useCallback(
    (formData: FormData, payNow: boolean, total: number, activeTab: OrderType) => {
      if (!selectedRestaurant?.restaurant_id) {
        console.error("No restaurant selected");
        return;
      }

      try {
        const orderRequest = transformCartToOrderRequest(
          cartItems,
          formData,
          selectedRestaurant.restaurant_id, // ✅ dynamic restaurant id
          payNow,
          total
        );

        dispatch(placeOrderRequest(orderRequest));
      } catch (error) {
        console.error("Error preparing order:", error);
      }
    },
    [cartItems, selectedRestaurant, dispatch]
  );

  const handleTakeoutOrder = useCallback(
    (formData: FormData, total: number, activeTab: OrderType) => {
      setSubmittingMethod("takeout");
      handleOrderSubmit(formData, false, total, activeTab);
    },
    [handleOrderSubmit]
  );

  const handlePayNowOrder = useCallback(
    (formData: FormData, total: number, activeTab: OrderType) => {
      setSubmittingMethod("pay_online");
      handleOrderSubmit(formData, true, total, activeTab);
    },
    [handleOrderSubmit]
  );

  const closePaymentPopup = useCallback(() => {
    closePopup();
  }, [closePopup]);

  const handleContinueShopping = useCallback(() => {
    setShowTakeoutSuccessPopup(false);
    dispatch(clearCart());
    navigate("/menu");
  }, [dispatch, navigate]);

  // Function to reset all popup states
  const resetAllPopupStates = useCallback(() => {
    setShowVerifyingPaymentPopup(false);
    setShowPaymentFailedPopup(false);
    setShowPaymentFailedProcessingPopup(false);
    setShowPaymentSuccessPopup(false);
    paymentRef.current = null;
    
    // Close any open popup
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
  }, []);

  // Handle payment success
  const handlePaymentSuccess = useCallback((onClose?: () => void) => {
    setShowPaymentSuccessPopup(false);
    // Reset payment progress state
    isPaymentInProgress.current = false;
    // Close the drawer or callback
    if (onClose) {
      onClose();
    }
  }, []);

  // Handle payment retry
  const handlePaymentRetry = useCallback((retryCallback?: () => void) => {
    setShowPaymentFailedPopup(false);
    setShowPaymentFailedProcessingPopup(false);
    // Reset payment progress state to allow new payment attempts
    isPaymentInProgress.current = false;
    // Execute retry callback (usually reopens the order popup or retries payment)
    if (retryCallback) {
      retryCallback();
    }
  }, []);

  // NEW: Handle delivery quote request
  const handleDeliveryQuote = useCallback(
    (formData: FormData, total: number, deliveryFee: number) => {
      if (!selectedRestaurant?.restaurant_id) {
        console.error("No restaurant selected");
        return;
      }

      // Store form data for step 2
      deliveryFormDataRef.current = { formData, total, deliveryFee };
      setSubmittingMethod("delivery");

      // Build delivery quote payload (WITHOUT delivery charge - API will calculate it)
      const quotePayload = {
        restaurant_id: selectedRestaurant.restaurant_id,
        name: formData.name,
        phone: formData.phone,
        email: formData.email || '',
        ordered_items: cartItems.map(item => ({
          name: item.item?.name || '',
          quantity: item.quantity,
          itemPrice: item.totalPrice,
          modifiers: [],
          spiceLevel: item.selected?.spice?.name || '',
          image: item.item?.image || ''
        })),
        grand_total: total,
        special_requests: formData.specialRequests || '',
        order_type: 'manual',
        delivery_type: 'delivery',
        pay_now: true,
        address: {
          streetAddress: formData.address_line_1 || '',
          city: formData.locality || '',
          state: formData.administrative_district_level_1 || '',
          zipCode: formData.postal_code || '',
          country: 'United States'
        }
      };

      console.log('Delivery quote payload:', JSON.stringify(quotePayload, null, 2));
      dispatch(createDeliveryQuoteRequest(quotePayload));
    },
    [selectedRestaurant, cartItems, dispatch]
  );

  // NEW: Handle delivery quote success - Show modal
  useEffect(() => {
    if (deliveryQuoteResponse && !deliveryQuoteLoading) {
      console.log('Delivery quote response received:', deliveryQuoteResponse);
      
      // Save delivery quote data before clearing
      if (deliveryQuoteResponse.deliveryQuote) {
        setSavedDeliveryQuote(deliveryQuoteResponse.deliveryQuote);
      }
      
      // Show delivery quote modal
      setShowDeliveryQuoteModal(true);
      setSubmittingMethod(null);
      
      // Clear delivery quote state
      dispatch(clearDeliveryQuoteState());
    }
  }, [deliveryQuoteResponse, deliveryQuoteLoading, dispatch]);

  // NEW: Handle Place Order from delivery quote modal
  const handlePlaceDeliveryOrder = useCallback(() => {
    if (!savedDeliveryQuote || !deliveryFormDataRef.current) {
      console.error('Missing delivery quote data', { savedDeliveryQuote, deliveryFormDataRef: deliveryFormDataRef.current });
      return;
    }

    const { deliveryId, deliveryChargeCents } = savedDeliveryQuote;
    const { formData, total } = deliveryFormDataRef.current;
    
    console.log('Placing delivery order with:', { deliveryId, deliveryChargeCents, total });

    // Close quote modal
    setShowDeliveryQuoteModal(false);
    setSubmittingMethod("delivery");

    // Build order payload with deliveryId
    const orderRequest = transformCartToOrderRequest(
      cartItems,
      formData,
      selectedRestaurant?.restaurant_id || '',
      true, // pay_now = true
      total + deliveryChargeCents
    );

    // Add deliveryId to the order request
    orderRequest.deliveryId = deliveryId;

    // Add delivery charge item to ordered_items
    orderRequest.ordered_items.push({
      name: 'Delivery Charge',
      quantity: 1,
      itemPrice: deliveryChargeCents.toString(),
      modifiers: [],
      modifier_price: '0',
      spice_level: '',
      image: '',
      total_item_price: deliveryChargeCents.toString()
    });

    // Clear delivery form data ref
    deliveryFormDataRef.current = null;

    console.log('Place order payload:', JSON.stringify(orderRequest, null, 2));

    // Dispatch place order
    dispatch(placeOrderRequest(orderRequest));
    
    // Clear saved delivery quote after dispatching
    setSavedDeliveryQuote(null);
  }, [savedDeliveryQuote, cartItems, selectedRestaurant, dispatch]);

  // NEW: Handle delivery quote errors
  useEffect(() => {
    if (deliveryQuoteError && !deliveryQuoteLoading) {
      console.error("Delivery quote error:", deliveryQuoteError);
      setSubmittingMethod(null);
      deliveryFormDataRef.current = null;
    }
  }, [deliveryQuoteError, deliveryQuoteLoading]);

  return {
    // State variables
    submittingMethod,
    showTakeoutSuccessPopup,
    showVerifyingPaymentPopup,
    showPaymentFailedPopup,
    showPaymentFailedProcessingPopup,
    showPaymentSuccessPopup,
    showDeliveryQuoteModal,
    showDeliverySuccessPopup,
    orderLoading,
    orderError,
    orderResponse,
    deliveryQuoteLoading,
    deliveryQuoteResponse,
    savedDeliveryQuote,
    
    // Handler functions
    handleTakeoutOrder,
    handlePayNowOrder,
    handleDeliveryQuote,
    handlePlaceDeliveryOrder,
    closePaymentPopup,
    handleContinueShopping,
    initiatePayment,
    handlePaymentSuccess,
    handlePaymentRetry,
    resetAllPopupStates,
    setShowDeliveryQuoteModal,
    setShowDeliverySuccessPopup,
    
    // Popup management
    openPaymentPopup,
    closePopup,
    
    // References
    popupRef,
    paymentRef
  };
};
